import { useMemo } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import BlankImage from '../../../../assets/images/blank-image.png';
import { Button } from '../../../../components/common/buttons/Button';
import { newProductPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { INewProduct } from '../../../../interfaces/NewProduct.interfaces';
import { getGeneralProductPrice, IProductForGetProductPrice } from '../../../../utils/getter/getProductPrice.util';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import { LoginToCheckPrice } from '../components/LoginToCheckPrice';
import { ProductStatusBanner } from '../components/ProductStatusBanner';
import { ProductStatusText } from '../components/ProductStatusText';

export const NewProductPreviewCard = ({
  product,
  renderedIn,
}: {
  product: INewProduct;
  renderedIn: 'how-about-new-product' | 'recommended-new-product';
}) => {
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const price = useMemo<string>(() => {
    return getGeneralProductPrice({ product: product as IProductForGetProductPrice, isUserLoggedIn });
  }, [product, isUserLoggedIn]);

  const inquiryButtonHandler = () => {
    const clickEventProperties = {
      id: product.id,
      title: product.title,
      price: product.price,
      installment_yn: product.installmentYn,
      negotiation_yn: product.negotiationYn,
      section: renderedIn,
    };

    ampli.pvmClickNewProductInquiry(clickEventProperties);
    ReactGA.event('pvm_click_new_product_inquiry', {
      clickEventProperties,
    });

    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: 'tel:0316074997' });
    } else {
      window.location.href = 'tel:0316074997';
    }
  };

  return (
    <Container
      onClick={(e) => {
        e.stopPropagation();
        ampli.pvmClickNewProductPreviewCard({
          id: product.id,
          title: product.title,
          price: product.price,
          installment_yn: product.installmentYn,
          negotiation_yn: product.negotiationYn,
          section: renderedIn,
        });
        navigate(`${newProductPageInfo.detailPage.path}/${product.id}`);
      }}
    >
      <ImageWrap>
        {product.status === 'SOLDOUT' && <ProductStatusText text={'판매 완료'} />}

        <Image
          src={
            product.images.filter((image) => image.type === 'LIST').length > 0
              ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
              : BlankImage
          }
          alt={
            product.images.filter((image) => image.type === 'LIST').length > 0
              ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
              : BlankImage
          }
          isBlurred={product.status === 'SOLDOUT'}
          loading={'lazy'}
        />

        {product.status === 'SOLDOUT' && <ProductStatusBanner productStatus={product.status} />}
      </ImageWrap>

      <ProductInfo isBlurred={product.status === 'SOLDOUT'}>
        <Title>{product.title}</Title>
        <PriceWrap>
          {isUserLoggedIn && product.negotiationYn && <Price>{'가격 문의'}</Price>}

          {isUserLoggedIn && !product.negotiationYn && (
            <>
              <Price>{price}</Price>

              {product.priceVisible && (
                <DiscountWrap>
                  <Discount>{'회원가'}</Discount>
                </DiscountWrap>
              )}
            </>
          )}
          {!isUserLoggedIn && <LoginToCheckPrice forPreviewCard />}
        </PriceWrap>
      </ProductInfo>

      <ButtonWrapper>
        {product.installmentYn && (
          <Button
            borderType={'square'}
            color={'outlinedGreyLight'}
            onClick={inquiryButtonHandler}
            size={'sm'}
            text={{ content: '할부 문의', size: 'md' }}
            riveAnimation={{ name: 'CircleWonAnimate', size: 'Large' }}
            icon={{ fileName: 'AngleRight', position: 'right', size: 'Medium', fill: '#505363' }}
          />
        )}
        {!product.installmentYn && (
          <Button
            borderType={'square'}
            color={'outlinedGreyLight'}
            onClick={inquiryButtonHandler}
            size={'sm'}
            text={{ content: '문의 하기', size: 'md' }}
            icon={{ fileName: 'AngleRight', position: 'right', size: 'Medium', fill: '#505363' }}
          />
        )}
      </ButtonWrapper>
    </Container>
  );
};

const Container = styled.div`
  min-width: 100%;
  overflow: visible;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  gap: 8px;

  z-index: 1;

  &:hover {
    cursor: pointer;
  }
`;

const ImageWrap = styled.div`
  width: 100%;

  padding-top: 128px;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const ProductInfo = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};

  gap: var(--radius-small, 4px);
`;

const Title = styled.h1`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */
`;

const PriceWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;

  gap: 4px;
`;

const Price = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  flex-shrink: 0;
  white-space: nowrap;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const ButtonWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;

  border-radius: var(--radius-medium, 8px);
`;

const DiscountWrap = styled.div`
  display: flex;
  align-items: center;
`;

const Discount = styled.span`
  color: var(--light-scheme-color-primary-red, #ff3049);
  /* Display/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 15.6px */
`;
